import { z } from "zod";

import {
  CIDR_FORMATS,
  EXPECTED_TYPE_NAMES,
  IP_FORMATS,
  NAMED_FORMATS,
  UUID_FORMATS,
} from "../constants/zod-error-messages";

// NOTE: NSAI-5511 — zod 4 rewrote the wording of every DEFAULT error message
// ("Required" became "Invalid input: expected string, received undefined",
// "String must contain at most 100 character(s)" became "Too big: expected
// string to have <=100 characters", and so on). A form field whose schema has
// no message of its own shows that default text verbatim, so the upgrade alone
// would have changed what users read under such fields on web, mobile and the
// extension. This map reproduces the zod 3 English defaults for the issues zod
// 3 could raise, and is installed as zod's global `customError`.
//
// A message a schema sets itself (`.min(1, t("..."))`, `{ error: ... }`, a
// refinement's message) always outranks the global map, because zod resolves
// the schema's own message first — so translated messages are untouched.
// Issues zod 3 had no equivalent for return `null` and fall through to zod
// 4's own locale text.

type TZodRawIssue = z.core.$ZodRawIssue;

interface IJoinValuesProps {
  values: readonly unknown[];
  separator?: string;
}

interface ISizeMessageProps {
  noun: string;
  unit: string;
  qualifier: string;
  limit: number | bigint;
}

// NOTE: zod 3's `util.joinValues` — strings quoted, everything else as
// `Array.prototype.join` prints it.
const joinValues = ({ values, separator = " | " }: IJoinValuesProps): string =>
  values
    .map((value) => (typeof value === "string" ? `'${value}'` : value))
    .join(separator);

const stringifyLiteral = (value: unknown): string =>
  JSON.stringify(value, (_key: string, item: unknown): unknown =>
    typeof item === "bigint" ? item.toString() : item,
  );

const isPromiseLike = (value: object): boolean =>
  "then" in value &&
  typeof value.then === "function" &&
  "catch" in value &&
  typeof value.catch === "function";

// NOTE: zod 3's `getParsedType`, which named the received type in its
// messages ("Expected number, received nan").
const getLegacyParsedType = (value: unknown): string => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "number") return Number.isNaN(value) ? "nan" : "number";
  if (typeof value !== "object") return typeof value;
  if (isPromiseLike(value)) return "promise";
  if (value instanceof Map) return "map";
  if (value instanceof Set) return "set";
  if (value instanceof Date) return "date";
  return "object";
};

const sizeMessage = ({
  noun,
  unit,
  qualifier,
  limit,
}: ISizeMessageProps): string =>
  `${noun} must contain ${qualifier} ${limit.toString()} ${unit}`;

const schemaTypeOf = (issue: TZodRawIssue): string | undefined => {
  const definition = issue.inst?._zod.def;
  return definition && "type" in definition ? definition.type : undefined;
};

// NOTE: zod 4 builds z.enum and z.nativeEnum as one enum schema. z.enum's
// entries map every value to itself; a native enum's do so only when each
// member is named after its value, and such an enum reads as a z.enum here.
const isNativeEnumIssue = (issue: TZodRawIssue): boolean => {
  const definition = issue.inst?._zod.def;
  if (!definition || !("entries" in definition)) return false;
  const { entries } = definition;
  if (typeof entries !== "object" || entries === null) return false;
  return Object.entries(entries).some(([key, value]) => key !== value);
};

const invalidTypeMessage = (
  issue: Extract<TZodRawIssue, { code: "invalid_type" }>,
): string => {
  if (issue.input === undefined) return "Required";

  if (issue.expected === "date" && issue.input instanceof Date) {
    return "Invalid date";
  }

  if (
    issue.expected === "number" &&
    typeof issue.input === "number" &&
    !Number.isNaN(issue.input)
  ) {
    return "Number must be finite";
  }

  const expected = EXPECTED_TYPE_NAMES[issue.expected] ?? issue.expected;
  const received =
    issue.expected === "int" && typeof issue.input === "number"
      ? "float"
      : getLegacyParsedType(issue.input);

  return `Expected ${expected}, received ${received}`;
};

const tooSmallMessage = (
  issue: Extract<TZodRawIssue, { code: "too_small" }>,
): string => {
  const isInclusive = issue.inclusive === true;

  if (issue.origin === "array") {
    return sizeMessage({
      noun: "Array",
      unit: "element(s)",
      qualifier: issue.exact
        ? "exactly"
        : isInclusive
          ? "at least"
          : "more than",
      limit: issue.minimum,
    });
  }

  if (issue.origin === "string") {
    return sizeMessage({
      noun: "String",
      unit: "character(s)",
      qualifier: issue.exact ? "exactly" : isInclusive ? "at least" : "over",
      limit: issue.minimum,
    });
  }

  const comparison = issue.exact
    ? "exactly equal to "
    : isInclusive
      ? "greater than or equal to "
      : "greater than ";

  if (
    issue.origin === "number" ||
    issue.origin === "int" ||
    issue.origin === "bigint"
  ) {
    return `Number must be ${comparison}${issue.minimum.toString()}`;
  }

  if (issue.origin === "date") {
    return `Date must be ${comparison}${new Date(Number(issue.minimum)).toString()}`;
  }

  return "Invalid input";
};

const tooBigMessage = (
  issue: Extract<TZodRawIssue, { code: "too_big" }>,
): string => {
  const isInclusive = issue.inclusive === true;

  if (issue.origin === "array") {
    return sizeMessage({
      noun: "Array",
      unit: "element(s)",
      qualifier: issue.exact
        ? "exactly"
        : isInclusive
          ? "at most"
          : "less than",
      limit: issue.maximum,
    });
  }

  if (issue.origin === "string") {
    return sizeMessage({
      noun: "String",
      unit: "character(s)",
      qualifier: issue.exact ? "exactly" : isInclusive ? "at most" : "under",
      limit: issue.maximum,
    });
  }

  const comparison = issue.exact
    ? "exactly"
    : isInclusive
      ? "less than or equal to"
      : "less than";

  if (issue.origin === "number" || issue.origin === "int") {
    return `Number must be ${comparison} ${issue.maximum.toString()}`;
  }

  if (issue.origin === "bigint") {
    return `BigInt must be ${comparison} ${issue.maximum.toString()}`;
  }

  if (issue.origin === "date") {
    const dateComparison = issue.exact
      ? "exactly"
      : isInclusive
        ? "smaller than or equal to"
        : "smaller than";

    return `Date must be ${dateComparison} ${new Date(Number(issue.maximum)).toString()}`;
  }

  return "Invalid input";
};

// NOTE: zod 4 keeps `.includes()`'s `position` on the check's definition, not
// on the issue; zod 3 named it in its message.
const includesPositionOf = (issue: TZodRawIssue): number | null => {
  const definition = issue.inst?._zod.def;
  const position =
    definition && "position" in definition ? definition.position : null;
  return typeof position === "number" ? position : null;
};

const invalidFormatMessage = (
  issue: Extract<TZodRawIssue, { code: "invalid_format" }>,
): string | null => {
  const { format } = issue;

  if (format === "starts_with" && typeof issue.prefix === "string") {
    return `Invalid input: must start with "${issue.prefix}"`;
  }
  if (format === "ends_with" && typeof issue.suffix === "string") {
    return `Invalid input: must end with "${issue.suffix}"`;
  }
  if (format === "includes" && typeof issue.includes === "string") {
    const position = includesPositionOf(issue);
    const positionText =
      position === null
        ? ""
        : ` at one or more positions greater than or equal to ${position.toString()}`;
    return `Invalid input: must include "${issue.includes}"${positionText}`;
  }
  if (format === "regex") return "Invalid";
  if (UUID_FORMATS.has(format)) return "Invalid uuid";
  if (IP_FORMATS.has(format)) return "Invalid ip";
  if (CIDR_FORMATS.has(format)) return "Invalid cidr";
  if (NAMED_FORMATS.has(format)) return `Invalid ${format}`;

  return null;
};

const invalidValueMessage = (
  issue: Extract<TZodRawIssue, { code: "invalid_value" }>,
): string => {
  const [firstValue] = issue.values;

  if (schemaTypeOf(issue) === "literal" && issue.values.length === 1) {
    return `Invalid literal value, expected ${stringifyLiteral(firstValue)}`;
  }

  // NOTE: zod 3 type-checked an enum before matching its options, so a value
  // that is not even a string (or a number, for a native enum) read as a type
  // error — "Required" when it was missing.
  const isOptionMiss =
    typeof issue.input === "string" ||
    (typeof issue.input === "number" && isNativeEnumIssue(issue));
  if (isOptionMiss) {
    return `Invalid enum value. Expected ${joinValues({ values: issue.values })}, received '${issue.input}'`;
  }

  if (issue.input === undefined) return "Required";

  return `Expected ${joinValues({ values: issue.values })}, received ${getLegacyParsedType(issue.input)}`;
};

const invalidUnionMessage = (
  issue: Extract<TZodRawIssue, { code: "invalid_union" }>,
): string | null => {
  if (issue.inclusive === false) return null;

  if (typeof issue.discriminator === "string" && Array.isArray(issue.options)) {
    return `Invalid discriminator value. Expected ${joinValues({ values: issue.options })}`;
  }

  return "Invalid input";
};

export const legacyZodErrorMap: z.core.$ZodErrorMap = (
  issue: TZodRawIssue,
): string | null => {
  switch (issue.code) {
    case "invalid_type":
      return invalidTypeMessage(issue);
    case "too_small":
      return tooSmallMessage(issue);
    case "too_big":
      return tooBigMessage(issue);
    case "invalid_format":
      return invalidFormatMessage(issue);
    case "not_multiple_of":
      return `Number must be a multiple of ${issue.divisor}`;
    case "unrecognized_keys":
      return `Unrecognized key(s) in object: ${joinValues({ values: issue.keys, separator: ", " })}`;
    case "invalid_union":
      return invalidUnionMessage(issue);
    case "invalid_value":
      return invalidValueMessage(issue);
    // NOTE: zod 3 reported a record key's or a set element's own issue at
    // its path; zod 4 wraps it, so the wrapped issue's message is surfaced.
    case "invalid_key":
    case "invalid_element":
      return issue.issues[0]?.message ?? null;
    case "custom":
      return "Invalid input";
    default:
      return null;
  }
};

// NOTE: Call once, before the first parse, in every client entry point (web,
// mobile, browser extension). zod 4 keeps its config on `globalThis`, so the
// one call covers every copy of zod a bundle may contain.
export const installLegacyZodErrorMessages = (): void => {
  z.config({ customError: legacyZodErrorMap });
};
